import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { IconButton } from './IconButton';

/** Root. Control it with `open` / `onOpenChange`, or use a `DialogTrigger`. */
export const Dialog = DialogPrimitive.Root;
/** Use `asChild` with a `Button`. Focus returns here when the dialog closes. */
export const DialogTrigger = DialogPrimitive.Trigger;
/** Closes the dialog. Use `asChild` with a `Button`. */
export const DialogClose = DialogPrimitive.Close;

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

export interface DialogContentProps extends Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
  'title'
> {
  /** Accessible name and visible heading. */
  title: ReactNode;
  /** Accessible description; visible unless `hideDescription`. */
  description: ReactNode;
  hideDescription?: boolean;
  size?: keyof typeof SIZES;
  /** Close button in the corner. Defaults to true. */
  showClose?: boolean;
  closeLabel?: string;
}

/**
 * Modal dialog: traps focus, closes on Escape or an outside click, and
 * returns focus to the trigger. Compose with `DialogBody` and `DialogFooter`.
 */
export const DialogContent = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent(
  {
    title,
    description,
    hideDescription = false,
    size = 'md',
    showClose = true,
    closeLabel = 'Close',
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-overlay/45 p-4 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in sm:p-6">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'relative flex w-full flex-col rounded-xl border border-border bg-surface-raised text-fg shadow-lg outline-none data-[state=closed]:animate-scale-out data-[state=open]:animate-scale-in',
            SIZES[size],
            className
          )}
          {...props}
        >
          <div className={cn('space-y-1 px-5 pb-4 pt-5', showClose && 'pr-14')}>
            <DialogPrimitive.Title className="text-base font-semibold leading-6 text-fg">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description
              className={cn(
                hideDescription ? 'sr-only' : 'text-sm text-fg-muted'
              )}
            >
              {description}
            </DialogPrimitive.Description>
          </div>
          {children}
          {showClose && (
            <DialogPrimitive.Close asChild>
              <IconButton
                aria-label={closeLabel}
                tooltip={false}
                icon={<X />}
                size="sm"
                className="absolute right-3 top-3"
              />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
});

/** Main content area under the dialog header. */
export const DialogBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 pb-5 text-sm', className)} {...props} />
);

/** Action row. Order buttons Cancel, then the confirming action last. */
export const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 rounded-b-xl border-t border-border bg-surface-sunken px-5 py-3 sm:flex-row sm:items-center sm:justify-end',
      className
    )}
    {...props}
  />
);
